import { useEffect, useMemo, useRef, useState } from "react";
import { api, WS_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GlassCard } from "@/components/common/GlassCard";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { MessageSquare, Plus, Send, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";

export default function MessagingPage() {
  const { user, token } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [wsStatus, setWsStatus] = useState("idle");
  const [showNew, setShowNew] = useState(false);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const r = await api.get("/threads");
      setThreads(r.data);
      if (!activeId && r.data[0]) setActiveId(r.data[0].id);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadThreads(); }, []);

  const loadMessages = async (tid) => {
    if (!tid) return;
    const r = await api.get(`/threads/${tid}/messages`);
    setMessages(r.data);
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 50);
  };
  useEffect(() => { loadMessages(activeId); }, [activeId]);

  // WebSocket lifecycle
  useEffect(() => {
    if (!activeId || !token) return;
    const url = `${WS_URL}?token=${encodeURIComponent(token)}&thread_id=${encodeURIComponent(activeId)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    setWsStatus("connecting");
    ws.onopen = () => setWsStatus("connected");
    ws.onclose = () => setWsStatus("disconnected");
    ws.onerror = () => setWsStatus("error");
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "message") {
          setMessages((m) => [...m, data.data]);
          setThreads((arr) =>
            arr.map((t) => t.id === activeId ? { ...t, last_message_preview: data.data.text.slice(0, 120), last_message_at: data.data.created_at } : t)
          );
          setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 30);
        }
      } catch {}
    };
    return () => { try { ws.close(); } catch {} };
  }, [activeId, token]);

  const send = async () => {
    if (!text.trim() || !activeId) return;
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify({ text }));
      setText("");
    } else {
      try {
        await api.post("/messages", { thread_id: activeId, text });
        await loadMessages(activeId);
        setText("");
      } catch (e) { toast.error("Failed to send"); }
    }
  };

  if (loading) return <LoadingState />;
  const active = threads.find((t) => t.id === activeId) || null;

  return (
    <div className="max-w-[1500px] mx-auto h-[calc(100vh-150px)]">
      <PageHeader
        title="Messaging"
        subtitle="Real-time chat between advisor, player, family, and trainer."
        actions={user?.role === "admin" ? <Button data-testid="messaging-new-thread" onClick={() => setShowNew(true)} className="cta-primary"><Plus className="h-4 w-4" /> New thread</Button> : null}
      />
      <div className="grid grid-cols-12 gap-5 h-[calc(100%-80px)]">
        <GlassCard className="col-span-12 lg:col-span-4 p-3 flex flex-col" testId="messaging-thread-list">
          <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.18em] text-3">Threads</div>
          <div className="flex-1 overflow-y-auto scrollbar-thin">
            {threads.length === 0 ? (
              <div className="p-3 text-2 text-sm">No threads yet.</div>
            ) : (
              threads.map((t) => (
                <button
                  key={t.id}
                  data-testid={`messaging-thread-item-${t.id}`}
                  onClick={() => setActiveId(t.id)}
                  className={`w-full text-left rounded-xl p-3 mb-1 ring-1 transition ${activeId === t.id ? "ring-cyan-300/30 bg-white/[0.08]" : "ring-white/5 hover:bg-white/[0.04]"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-1 text-sm font-medium truncate">{t.title}</div>
                    {t.last_message_at && <div className="text-3 text-[10px] font-mono">{new Date(t.last_message_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>}
                  </div>
                  <div className="text-3 text-xs mt-1 truncate">{t.last_message_preview || "No messages yet."}</div>
                </button>
              ))
            )}
          </div>
        </GlassCard>

        <GlassCard className="col-span-12 lg:col-span-8 flex flex-col" testId="messaging-chat-pane">
          {active ? (
            <>
              <div className="flex items-center gap-3 p-4 border-b border-white/10">
                <div className="h-9 w-9 rounded-xl bg-white/5 ring-1 ring-white/10 grid place-items-center"><MessageSquare className="h-4 w-4 text-cyan-300" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-1 font-semibold truncate">{active.title}</div>
                  <div className="text-3 text-xs">{active.participant_ids.length} participants</div>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  {wsStatus === "connected" ? <><Wifi className="h-3.5 w-3.5 text-emerald-300" /><span className="text-emerald-300">Live</span></> : <><WifiOff className="h-3.5 w-3.5 text-3" /><span className="text-3">{wsStatus}</span></>}
                </div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-3">
                {messages.map((m) => {
                  const me = m.sender_id === user?.id;
                  return (
                    <div key={m.id} data-testid={`message-${m.id}`} className={`flex ${me ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ring-1 ${me ? "bg-cyan-400/10 ring-cyan-300/25" : "bg-white/[0.06] ring-white/10"}`}>
                        <div className="flex items-center gap-2 text-[11px] text-3">
                          <span className="font-medium text-2">{m.sender_name}</span>
                          <span className="font-mono">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                        <div className="text-1 text-sm mt-1 leading-relaxed whitespace-pre-wrap">{m.text}</div>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && <div className="text-center text-2 text-sm py-12">No messages yet. Say hi.</div>}
              </div>
              <div className="p-3 border-t border-white/10 flex items-center gap-2">
                <Input
                  data-testid="messaging-message-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                  placeholder="Type a message…"
                  className="bg-white/5 border-white/10 text-1"
                />
                <Button data-testid="messaging-send-button" onClick={send} className="cta-primary"><Send className="h-4 w-4" /></Button>
              </div>
            </>
          ) : (
            <div className="flex-1 grid place-items-center">
              <EmptyState icon={MessageSquare} title="Select a thread" description="Choose a conversation on the left." />
            </div>
          )}
        </GlassCard>
      </div>

      {showNew && <NewThreadDialog open={showNew} onClose={() => setShowNew(false)} onCreated={(t) => { setThreads((arr) => [t, ...arr]); setActiveId(t.id); }} />}
    </div>
  );
}

function NewThreadDialog({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState({});
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  const submit = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    try {
      const r = await api.post("/threads", { title, participant_ids: ids });
      onCreated(r.data);
      onClose();
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[color:var(--canvas-2)]/95 backdrop-blur-2xl border-white/10" data-testid="new-thread-dialog">
        <DialogHeader><DialogTitle className="text-1">New thread</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-2 text-xs">Title</Label>
            <Input data-testid="new-thread-title" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-white/5 border-white/10 text-1" />
          </div>
          <div>
            <Label className="text-2 text-xs">Participants</Label>
            <div className="mt-2 max-h-[260px] overflow-y-auto scrollbar-thin space-y-1">
              {users.map((u) => (
                <label key={u.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-white/[0.04]">
                  <Checkbox checked={!!selected[u.id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [u.id]: !!v }))} />
                  <div className="flex-1">
                    <div className="text-1 text-sm">{u.full_name}</div>
                    <div className="text-3 text-[11px]">{u.email} · {u.role}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-2">Cancel</Button>
          <Button data-testid="new-thread-create" onClick={submit} className="cta-primary">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
